import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { SettingsService } from 'src/app/services/settings.service';
import { SharedFeaturesService } from 'src/app/services/shared-features.service';
import { WebSocketService } from 'src/app/services/web-socket.service';
import {
  TaskDetailsDialogComponent,
  TaskDetailsData,
} from './task-details-dialog.component';

@Component({
  selector: 'app-celery-task-view',
  template: `
    <div class="tasks-page">
      <!-- Page header -->
      <div class="pagehead">
        <h1>Task history</h1>
        <p>Every job run across your solutions.</p>
      </div>

      <!-- Toolbar -->
      <div class="toolbar">
        <div class="filters">
          <button
            type="button"
            [class.active]="statusValueToShow === ''"
            (click)="setStatus('')"
          >
            All
          </button>
          <button
            type="button"
            [class.active]="statusValueToShow === 'SUCCESS'"
            (click)="setStatus('SUCCESS')"
          >
            Success
          </button>
          <button
            type="button"
            [class.active]="statusValueToShow === 'FAILURE'"
            (click)="setStatus('FAILURE')"
          >
            Failure
          </button>
          <button
            type="button"
            [class.active]="statusValueToShow === 'PENDING'"
            (click)="setStatus('PENDING')"
          >
            Pending
          </button>
        </div>

        <div class="right">
          <div class="searchbox">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="7"/><path d="m20 20-3-3"/></svg>
            <input
              type="text"
              placeholder="Search runs"
              [value]="searchTerm"
              (input)="onSearch($any($event.target).value)"
            />
          </div>
          <div class="count">{{ rangeLabel }}</div>
        </div>
      </div>

      <!-- Table panel -->
      <div class="panel">
        <table mat-table [dataSource]="dataSource" class="tasks-table">
          <!-- RUN -->
          <ng-container matColumnDef="run">
            <th mat-header-cell *matHeaderCellDef>Run</th>
            <td mat-cell *matCellDef="let element">
              <div class="run-nm">{{ runName(element.task_name) }}</div>
              <div class="run-sub">
                #{{ shortId(element.task_id) }} · solutions run
              </div>
            </td>
          </ng-container>

          <!-- STATUS -->
          <ng-container matColumnDef="status">
            <th mat-header-cell *matHeaderCellDef>Status</th>
            <td mat-cell *matCellDef="let element">
              <span class="badge" [ngClass]="badgeClass(element.status)">
                {{ statusLabel(element.status) }}
              </span>
            </td>
          </ng-container>

          <!-- STARTED -->
          <ng-container matColumnDef="started">
            <th mat-header-cell *matHeaderCellDef>Started</th>
            <td mat-cell *matCellDef="let element" class="muted">
              {{ element.date_created | date : 'MMM d, h:mm a' }}
            </td>
          </ng-container>

          <!-- RESULT -->
          <ng-container matColumnDef="result">
            <th mat-header-cell *matHeaderCellDef>Result</th>
            <td mat-cell *matCellDef="let element">
              <span
                class="result"
                [class.fail]="element.status === 'FAILURE'"
                [matTooltip]="resultSummary(element)"
              >
                {{ resultSummary(element) }}
              </span>
            </td>
          </ng-container>

          <!-- DETAILS -->
          <ng-container matColumnDef="details">
            <th mat-header-cell *matHeaderCellDef></th>
            <td mat-cell *matCellDef="let element" class="details-cell">
              <button
                type="button"
                class="details"
                (click)="openDetails(element)"
              >
                Details ›
              </button>
            </td>
          </ng-container>

          <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
          <tr mat-row *matRowDef="let row; columns: displayedColumns"></tr>

          <tr class="empty-row" *matNoDataRow>
            <td class="empty-cell" [attr.colspan]="displayedColumns.length">
              No task runs found.
            </td>
          </tr>
        </table>

        <mat-paginator
          [pageSize]="25"
          [pageSizeOptions]="[10, 25, 50, 100]"
          (page)="onPage()"
          showFirstLastButtons
        ></mat-paginator>
      </div>
    </div>
  `,
  styleUrls: ['./celery-task-view.component.scss'],
})
export class CeleryTaskViewComponent implements OnInit, AfterViewInit, OnDestroy {
  displayedColumns: string[] = [
    'run',
    'status',
    'started',
    'result',
    'details',
  ];

  dataSource = new MatTableDataSource<any>([]);
  statusValueToShow = '';
  searchTerm = '';

  @ViewChild(MatPaginator) paginator!: MatPaginator;

  private taskStatusSub?: Subscription;

  constructor(
    private settingServices: SettingsService,
    private sharedServices: SharedFeaturesService,
    private webSocketService: WebSocketService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    // Combined predicate: status filter AND free-text search.
    this.dataSource.filterPredicate = (row: any, _filter: string) => {
      const statusOk =
        !this.statusValueToShow || row.status === this.statusValueToShow;
      if (!statusOk) {
        return false;
      }
      const term = this.searchTerm.trim().toLowerCase();
      if (!term) {
        return true;
      }
      const haystack = [
        this.runName(row.task_name),
        this.resultSummary(row),
        row.status,
        row.task_id,
      ]
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    };

    this.fetchTasks();

    // Live-refresh when a run reaches a terminal state (pushed app-wide
    // over the shared socket; connection is owned by AppComponent).
    // Debounced: a batch of runs finishing together would otherwise fire one
    // full refetch per message, and the responses can land out of order.
    this.webSocketService.connect();
    this.taskStatusSub = this.webSocketService
      .getMessages()
      .pipe(
        filter((response: any) => response?.message?.type === 'task_status'),
        debounceTime(500)
      )
      .subscribe(() => this.fetchTasks());
  }

  private fetchTasks(): void {
    this.settingServices.getCeleryTask().subscribe({
      next: (response: any) => {
        this.dataSource.data = response || [];
        this.applyFilter();
      },
      error: (error: any) => {
        console.error('An error occurred while fetching task', error);
      },
    });
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
  }

  ngOnDestroy(): void {
    this.taskStatusSub?.unsubscribe();
  }

  // ---- filter / search wiring -------------------------------------------

  setStatus(status: string): void {
    this.statusValueToShow = status;
    this.applyFilter();
  }

  onSearch(value: string): void {
    this.searchTerm = value || '';
    this.applyFilter();
  }

  private applyFilter(): void {
    // The filter string just needs to change so MatTableDataSource re-runs
    // the predicate; the predicate reads the real models off `this`.
    this.dataSource.filter = `${this.statusValueToShow}|${this.searchTerm}`.trim() || ' ';
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  onPage(): void {
    // hook for range label recompute (getter reads paginator state directly)
  }

  // ---- display helpers ---------------------------------------------------

  /** Prettify the last dotted segment of the task name. */
  runName(taskName: string): string {
    if (!taskName) {
      return 'Solution run';
    }
    const last = taskName.split('.').pop() || taskName;
    return last
      .split('_')
      .filter((w) => w.length)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ')
      .replace(/^Run Version Of /, '')
      // task_name is uniform -> "run_version_of_solution" => "Solution run"
      .replace(/^Run Version Of Solution$/, 'Solution run');
  }

  shortId(taskId: string): string {
    return (taskId || '').slice(0, 4);
  }

  statusLabel(status: string): string {
    if (!status) {
      return '';
    }
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  }

  badgeClass(status: string): string {
    switch (status) {
      case 'SUCCESS':
        return 'ok';
      case 'FAILURE':
        return 'bad';
      default:
        return 'pend';
    }
  }

  /** Human-friendly one-line summary of the task result. */
  resultSummary(element: any): string {
    const status = element?.status;
    const result = element?.result;

    if (status === 'SUCCESS') {
      if (
        typeof result === 'string' &&
        result.startsWith('Pipeline run successfully')
      ) {
        return 'Pipeline ran successfully';
      }
      return typeof result === 'string' && result
        ? result
        : 'Pipeline ran successfully';
    }

    if (status === 'FAILURE') {
      try {
        const parsed = JSON.parse(result);
        if (parsed && (parsed.exc_type || parsed.exc_message)) {
          let msg = parsed.exc_message;
          if (Array.isArray(msg)) {
            msg = msg[0];
          }
          const type = parsed.exc_type || 'Error';
          return msg ? `${type} — ${msg}` : type;
        }
      } catch {
        /* fall through */
      }
      return 'Run failed';
    }

    return 'Queued…';
  }

  truncate(text: string, max = 60): string {
    if (!text) {
      return '';
    }
    return text.length > max ? text.slice(0, max - 1).trimEnd() + '…' : text;
  }

  // ---- count / range label ----------------------------------------------

  get rangeLabel(): string {
    const total = this.dataSource.filteredData.length;
    if (!total) {
      return '0 of 0';
    }
    const p = this.dataSource.paginator;
    if (!p) {
      return `1–${total} of ${total}`;
    }
    const start = p.pageIndex * p.pageSize + 1;
    const end = Math.min(start + p.pageSize - 1, total);
    return `${start}–${end} of ${total}`;
  }

  // ---- details dialog ----------------------------------------------------

  openDetails(element: any): void {
    const data: TaskDetailsData = {
      task_id: element.task_id,
      status: element.status,
      date_created: element.date_created,
      date_done: element.date_done,
      result: element.result,
      traceback: element.traceback ?? null,
      summary: this.resultSummary(element),
    };
    this.dialog.open(TaskDetailsDialogComponent, {
      data,
      width: '620px',
      maxWidth: '92vw',
      panelClass: 'task-details-dialog',
      autoFocus: false,
    });
  }
}
